import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mainSiteUrl } from "@/mocks/categories";
import { pillarArticles } from "@/mocks/articles";

const categoryMap: Record<string, string> = {
  "Guide": "category.guide",
  "Review": "category.review",
  "Safety": "category.safety",
  "Shipping": "category.shipping",
  "Warehouse": "category.warehouse",
  "QC": "category.qc",
  "Coupons": "category.coupons",
  "Comparison": "category.comparison",
  "Beginners": "category.beginners",
};

const whyPoints = [
  { icon: "ri-layout-grid-line", titleKey: "why.point1.title", descKey: "why.point1.desc" },
  { icon: "ri-book-open-line", titleKey: "why.point2.title", descKey: "why.point2.desc" },
  { icon: "ri-shield-check-line", titleKey: "why.point3.title", descKey: "why.point3.desc" },
  { icon: "ri-arrow-right-up-line", titleKey: "why.point4.title", descKey: "why.point4.desc" },
];

export default function InsightSections() {
  const { t, i18n } = useTranslation();
  const detected = i18n.language || "en";
  const lang = (detected.split("-")[0] as "en" | "fr" | "da" | "de") || "en";

  return (
    <>
      <section className="mx-auto max-w-[1280px] px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
            {t("why.title")}
          </h2>
          <p className="mt-3 text-foreground-600 leading-relaxed">
            {t("why.subtitle")}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {whyPoints.map((point) => (
            <div
              key={point.titleKey}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className={point.icon} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                {t(point.titleKey)}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {t(point.descKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section id="articles" className="bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
              {t("articles.title")}
            </h2>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              {t("articles.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillarArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={`${article.title[lang]} — AllChinaBuy Spreadsheet`}
                    title={`${article.title[lang]} AllChinaBuy Spreadsheet`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-primary-700">
                    {t(categoryMap[article.category] || "category.guide")}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug">
                    {article.title[lang]}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {article.intro[lang]}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 group-hover:text-primary-800 whitespace-nowrap">
                    {t("articles.readMore")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 md:px-6 py-16 md:py-20">
        <div className="rounded-2xl bg-primary-500 px-6 py-12 md:px-12 md:py-16 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-background-50">
            {t("cta.title")}
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-background-100 leading-relaxed">
            {t("cta.text")}
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            {t("cta.button")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line" />
            </span>
          </a>
        </div>
      </section>
    </>
  );
}